import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { PlayCircle, ListChecks, Flag, Heart, Sun, Moon, ExternalLink, Eye } from 'lucide-react';

const PAGES = [
  { step: 0, label: 'Start', icon: PlayCircle, desc: 'Basics, video & AI build inputs' },
  { step: 1, label: 'Details', icon: ListChecks, desc: 'Qualifying Qs, FAQs & uploads' },
  { step: 2, label: 'Last step', icon: Flag, desc: 'Twilio A2P & business details' },
  { step: 3, label: 'Thank You', icon: Heart, desc: 'Personalized confirmation' },
];

export default function OnboardingPreviewPage() {
  const { slug } = useParams();
  const [activeStep, setActiveStep] = useState(0);
  const [bgTheme, setBgTheme] = useState('');
  const iframeRef = useRef(null);

  const push = useCallback((step, theme) => {
    const w = iframeRef.current?.contentWindow;
    if (!w) return;
    w.postMessage({ type: 'ob_preview_step', step }, '*');
    w.postMessage({ type: 'ob_preview_theme', theme: theme || '' }, '*');
  }, []);

  useEffect(() => { push(activeStep, bgTheme); }, [activeStep, bgTheme, push]);

  useEffect(() => {
    const onMsg = (e) => { if (e.data?.type === 'ob_preview_ready') push(activeStep, bgTheme); };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [activeStep, bgTheme, push]);

  return (
    <div className="min-h-screen flex" style={{ background: '#08090d' }} data-testid="onboarding-preview-page">
      {/* Left nav */}
      <aside className="w-72 shrink-0 border-r border-white/[0.08] flex flex-col" style={{ background: '#0c0d12' }}>
        <div className="p-5 border-b border-white/[0.08]">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-sky-400 to-violet-500 flex items-center justify-center">
              <Eye className="text-white" size={17} />
            </div>
            <div>
              <p className="text-white font-semibold text-sm">Form Preview</p>
              <p className="text-gray-500 text-xs">Exactly what your clients will see</p>
            </div>
          </div>
        </div>

        <div className="p-4 flex-1">
          <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2 px-1">Form pages</p>
          <div className="space-y-1.5">
            {PAGES.map(p => (
              <button
                key={p.step}
                onClick={() => setActiveStep(p.step)}
                className={`w-full text-left px-3 py-3 rounded-xl border transition-all flex items-start gap-3 ${
                  activeStep === p.step
                    ? 'bg-teal-400/10 border-teal-400/35'
                    : 'border-transparent hover:bg-white/[0.04]'
                }`}
                data-testid={`preview-nav-step-${p.step}`}
              >
                <p.icon size={17} className={`mt-0.5 shrink-0 ${activeStep === p.step ? 'text-teal-400' : 'text-gray-500'}`} />
                <span>
                  <span className={`block text-sm font-medium ${activeStep === p.step ? 'text-teal-400' : 'text-gray-300'}`}>{p.label}</span>
                  <span className="block text-[11px] text-gray-500">{p.desc}</span>
                </span>
              </button>
            ))}
          </div>

          <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mt-6 mb-2 px-1">Background</p>
          <div className="flex gap-2">
            {[{ v: 'dark', icon: Moon, l: 'Dark' }, { v: 'light', icon: Sun, l: 'Light' }].map(o => {
              const active = (bgTheme || 'dark') === o.v;
              return (
                <button
                  key={o.v}
                  onClick={() => setBgTheme(o.v)}
                  className={`flex-1 px-3 py-2.5 rounded-xl border text-sm flex items-center justify-center gap-2 transition-all ${
                    active
                      ? 'border-teal-400/40 bg-teal-400/10 text-teal-400'
                      : 'border-white/10 text-gray-400 hover:border-white/25'
                  }`}
                  data-testid={`preview-theme-${o.v}`}
                >
                  <o.icon size={14} /> {o.l}
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-4 border-t border-white/[0.08]">
          <a
            href={`/onboard/${slug}`}
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-gradient-to-r from-teal-400 to-teal-500 text-[#08090d] text-sm font-semibold hover:shadow-lg hover:shadow-teal-400/25 transition-all"
            data-testid="preview-open-live"
          >
            <ExternalLink size={14} /> Open real form
          </a>
        </div>
      </aside>

      {/* Form */}
      <main className="flex-1 min-w-0">
        <iframe
          ref={iframeRef}
          src={`/onboard/${slug}?embed=1&preview=1`}
          title="Onboarding form preview"
          className="w-full h-screen"
          style={{ border: 'none' }}
          onLoad={() => push(activeStep, bgTheme)}
          data-testid="preview-form-iframe"
        />
      </main>
    </div>
  );
}
